import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getCandidateProfile } from '../services/api';
import CreditCostBadge from '../components/molecules/CreditCostBadge';
import useCredits from '../hooks/useCredits';
import { API_BASE_URL } from '../config';

const LiveAITrainerPage = () => {
  const navigate = useNavigate();
  const [currentSession, setCurrentSession] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Credit system
  const { balance, hasEnoughCredits, getCost, deductCreditsForService } = useCredits();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [profileData, setProfileData] = useState(null);
  
  // Training mode selection
  const [trainingMode, setTrainingMode] = useState('normal'); // 'normal' or 'live'
  
  // Form state
  const [selectedRole, setSelectedRole] = useState('');
  const [customJD, setCustomJD] = useState('');
  const [uploadedJD, setUploadedJD] = useState(null);
  const [difficultyLevel, setDifficultyLevel] = useState('medium');
  const [duration, setDuration] = useState(10); // For live mode
  const [numQuestions, setNumQuestions] = useState(10); // For normal mode
  const [language, setLanguage] = useState('en-US');
  const [resumeFile, setResumeFile] = useState(null);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('female-1');

  // Voice options for live mode
  const voiceOptions = [
    { value: 'female-1', label: 'Female - Expressive (US)', description: 'Breezy, expressive female voice' },
    { value: 'female-2', label: 'Female - Authoritative (UK)', description: 'Firm, professional female voice' },
    { value: 'male-1', label: 'Male - Energetic (US)', description: 'Upbeat, friendly male voice' },
    { value: 'male-2', label: 'Male - Professional (UK)', description: 'Calm, professional male voice' },
    { value: 'male-indian-1', label: 'Male - Indian Accent', description: 'Upbeat male voice with Indian accent' },
  ];

  // Predefined roles with generic prompts
  const roles = [
    { value: 'software_engineer', label: 'Software Engineer', icon: 'code' },
    { value: 'data_scientist', label: 'Data Scientist', icon: 'analytics' },
    { value: 'product_manager', label: 'Product Manager', icon: 'inventory' },
    { value: 'business_analyst', label: 'Business Analyst', icon: 'assessment' },
    { value: 'marketing_manager', label: 'Marketing Manager', icon: 'campaign' },
    { value: 'sales_executive', label: 'Sales Executive', icon: 'trending_up' },
    { value: 'hr_manager', label: 'HR Manager', icon: 'people' },
    { value: 'financial_analyst', label: 'Financial Analyst', icon: 'account_balance' },
    { value: 'ui_ux_designer', label: 'UI/UX Designer', icon: 'design_services' },
    { value: 'project_manager', label: 'Project Manager', icon: 'task' }
  ];

  // Difficulty levels
  const difficultyLevels = [
    { value: 'easy', label: 'Easy', description: 'Basic questions for beginners' },
    { value: 'medium', label: 'Medium', description: 'Intermediate level questions' },
    { value: 'expert', label: 'Expert', description: 'Advanced challenging questions' }
  ];

  // Duration options for live mode
  const durationOptions = [5, 10, 15, 20, 30];
  
  // Question count options for normal mode
  const questionOptions = [5, 10, 15, 20];

  // Language options
  const languageOptions = [
    { value: 'en-US', label: 'English (US)', flag: '🇺🇸' },
    { value: 'en-IN', label: 'English (India)', flag: '🇮🇳' },
    { value: 'hi-IN', label: 'Hindi', flag: '🇮🇳' },
    { value: 'ta-IN', label: 'Tamil', flag: '🇮🇳' },
    { value: 'es-US', label: 'Spanish', flag: '🇪🇸' },
    { value: 'fr-FR', label: 'French', flag: '🇫🇷' },
    { value: 'de-DE', label: 'German', flag: '🇩🇪' },
    { value: 'ja-JP', label: 'Japanese', flag: '🇯🇵' },
    { value: 'ko-KR', label: 'Korean', flag: '🇰🇷' },
    { value: 'cmn-CN', label: 'Chinese (Mandarin)', flag: '🇨🇳' }
  ];

  useEffect(() => {
    const session = SessionManager.getCurrentSession();
    
    if (!session || session.role !== 'candidate') {
      // Allow guest access for training
      setLoading(false);
      return;
    }
    
    setCurrentSession(session);
    loadCandidateProfile(session.id);
    checkProfileCompletion();
  }, []);

  const checkProfileCompletion = async () => {
    const session = SessionManager.getCurrentSession();
    if (!session) return;

    try {
      const response = await fetch(`${API_BASE_URL}/candidates/${session.user_id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const profile = data.profile || data;
        setProfileData(profile);
        
        // Profile is complete if has full_name, phone, and at least one detail section
        const hasBasicInfo = profile.full_name && profile.phone;
        const hasDetails = (profile.education && profile.education.length > 0) ||
                          (profile.work_experience && profile.work_experience.length > 0) ||
                          (profile.skills && profile.skills.length > 0);
        
        setIsProfileComplete(hasBasicInfo && hasDetails);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const loadCandidateProfile = async (candidateId) => {
    try {
      const profile = await getCandidateProfile(candidateId);
      setCandidateProfile(profile);
    } catch (error) {
      console.error('Error loading candidate profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJDUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedJD(file);
      // Read file content
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomJD(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResumeFile(file);
    }
  };

  const handleStartTraining = async () => {
    // Validation
    if (!selectedRole) {
      alert('Please select a role');
      return;
    }

    // Check credits based on training mode
    const serviceType = trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer';
    if (!hasEnoughCredits(serviceType)) {
      setShowCreditModal(true);
      return;
    }

    // Deduct credits
    const creditResult = await deductCreditsForService(serviceType);
    if (!creditResult.success) {
      setShowCreditModal(true);
      return;
    }

    const selectedRoleData = roles.find(r => r.value === selectedRole);
    const mockApplicationId = `training_${Date.now()}`;
    
    // Prepare configuration
    const trainingConfig = {
      interviewType: 'trainer',
      trainingMode: trainingMode,
      language: language,
      duration: trainingMode === 'live' ? duration : null,
      numQuestions: trainingMode === 'normal' ? numQuestions : null,
      jobTitle: selectedRoleData.label,
      jobDescription: customJD || `Generic ${selectedRoleData.label} interview questions`,
      difficultyLevel: difficultyLevel,
      company: 'Training Session',
      candidateName: candidateProfile?.full_name || currentSession?.profile?.first_name || 'Candidate',
      candidateEmail: candidateProfile?.email || currentSession?.email || 'guest@example.com',
      candidateId: currentSession?.user_id || '',
      voice: selectedVoice,
      resumeText: candidateProfile?.resume_text || '',
      resumeFilename: candidateProfile?.resume_filename || resumeFile?.name || null,
      hasResume: !!(candidateProfile?.resume_url || resumeFile),
      sessionId: mockApplicationId
    };

    console.log('🚀 Starting training with config:', trainingConfig);

    // Navigate based on mode
    if (trainingMode === 'live') {
      navigate(`/live-interview/${mockApplicationId}`, { state: trainingConfig });
    } else {
      navigate('/ai-trainer/session', { state: trainingConfig });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <Icon name="school" size={32} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
            🎯 AI Interview Trainer
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
            Practice your interview skills with AI-powered questions and get detailed feedback
          </p>
          {/* Credit Cost Display */}
          <div className="flex justify-center">
            <CreditCostBadge 
              serviceType={trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer'}
              serviceName={trainingMode === 'live' ? 'AI Live Trainer' : 'AI Trainer'}
              size="md"
            />
          </div>
        </div>

        {/* Training Mode Selection - Cards */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Training Experience</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Normal Training Card */}
            <div
              onClick={() => setTrainingMode('normal')}
              className={`relative cursor-pointer rounded-2xl border-2 p-6 transition-all ${
                trainingMode === 'normal'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="quiz" size={32} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Mock Interview
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Practice with customized AI interviewer
                  </p>
                </div>
                {trainingMode === 'normal' && (
                  <Icon name="check_circle" size={24} className="text-blue-500" />
                )}
              </div>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>Text-based questions with detailed written feedback</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>No timer - take your time to answer</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>Select number of questions (5-20)</span>
                </div>
              </div>
            </div>

            {/* Live Training Card */}
            <div
              onClick={() => setTrainingMode('live')}
              className={`relative cursor-pointer rounded-2xl border-2 p-6 transition-all ${
                trainingMode === 'live'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-lg'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full">
                  NEW
                </span>
              </div>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="record_voice_over" size={32} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Live Interview
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Ready to go? Copilot will be by your side for your real interview
                  </p>
                </div>
                {trainingMode === 'live' && (
                  <Icon name="check_circle" size={24} className="text-purple-500" />
                )}
              </div>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>Real-time voice conversation with AI trainer</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>Timed session - simulate real interview</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="check" size={16} className="text-green-500" />
                  <span>Instant feedback and tips during conversation</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Training Configuration Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Interview Contest</h2>
          
          {/* Role Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select your role</option>
              {roles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Level */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {difficultyLevels.map((level) => (
                <button
                  key={level.value}
                  onClick={() => setDifficultyLevel(level.value)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficultyLevel === level.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900 dark:text-white mb-1">
                    {level.label}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {level.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Time/Questions Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {trainingMode === 'live' ? 'Duration' : 'Number of Questions'}
            </label>
            {trainingMode === 'live' ? (
              <div className="grid grid-cols-5 gap-3">
                {durationOptions.map((min) => (
                  <button
                    key={min}
                    onClick={() => setDuration(min)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      duration === min
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-bold text-gray-900 dark:text-white">{min}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">min</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {questionOptions.map((num) => (
                  <button
                    key={num}
                    onClick={() => setNumQuestions(num)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      numQuestions === num
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-bold text-gray-900 dark:text-white">{num}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">questions</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Voice Selection - Only for live mode */}
          {trainingMode === 'live' && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Interviewer Voice
              </label>
              <select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {voiceOptions.map((voice) => (
                  <option key={voice.value} value={voice.value}>
                    {voice.label} - {voice.description}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Resume Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Resume
            </label>
            {candidateProfile?.resume_url ? (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="description" size={24} className="text-green-600" />
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {candidateProfile.resume_filename || 'Your Resume'}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Existing resume will be used
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowResumeUpload(true)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  id="resume-upload"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeUpload}
                  className="hidden"
                />
                <label
                  htmlFor="resume-upload"
                  className="flex items-center justify-center gap-3 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all"
                >
                  <Icon name="upload_file" size={32} className="text-gray-400" />
                  <div className="text-center">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {resumeFile ? resumeFile.name : 'Upload Resume (Optional)'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      PDF, DOC, DOCX up to 10MB
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Job Description Section */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Job Description (Optional)
            </label>
            <div className="space-y-3">
              <textarea
                value={customJD}
                onChange={(e) => setCustomJD(e.target.value)}
                placeholder="Paste job description here for more targeted questions..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm">OR</div>
              <div>
                <input
                  type="file"
                  id="jd-upload"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleJDUpload}
                  className="hidden"
                />
                <label
                  htmlFor="jd-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all"
                >
                  <Icon name="upload" size={20} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {uploadedJD ? uploadedJD.name : 'Upload JD File'}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <Button
            size="xl"
            onClick={handleStartTraining}
            disabled={!selectedRole}
            className={`w-full py-4 ${
              trainingMode === 'live'
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Icon name={trainingMode === 'live' ? 'call' : 'play_arrow'} size={24} className="mr-2" />
            {trainingMode === 'live' ? 'Start Live Interview' : 'Start Mock Interview'}
          </Button>
        </div>
      </div>

      {/* Profile Completion / Credits Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0a1929] to-[#1e3a5f] rounded-2xl shadow-2xl max-w-md w-full p-8 relative border border-blue-500/20">
            {/* Close button */}
            <button
              onClick={() => setShowCreditModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <Icon name="close" size={24} />
            </button>

            {!isProfileComplete ? (
              /* Profile Incomplete - Show Complete Profile Message */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="person_add" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Complete Your Profile</h3>
                  <p className="text-gray-300 text-sm">
                    Get 50 free credits when you complete your profile!
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex items-start space-x-3">
                    <Icon name="stars" size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">What you'll get:</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>50 free credits instantly</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Access to all AI features</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Better job recommendations</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/candidate/profile/edit')}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Complete Profile Now
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Maybe Later
                  </Button>
                </div>
              </>
            ) : (
              /* Profile Complete - Redirect to Payment */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="account_balance_wallet" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Insufficient Credits</h3>
                  <p className="text-gray-300 text-sm">
                    You need {getCost(trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer')} credits for {trainingMode === 'live' ? 'AI Live Trainer' : 'AI Trainer'}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300 text-sm">Available Credits:</span>
                    <span className="text-white font-bold text-lg">{balance}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Required Credits:</span>
                    <span className="text-orange-400 font-bold text-lg">{getCost(trainingMode === 'live' ? 'ai_live_trainer' : 'ai_trainer')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/payment')}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Buy Credits
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveAITrainerPage;
